import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

/**
 * Phase 13 · Luxury-biotech buttons.
 *   • 14 px radius, 16 px 600-weight label, generous padding.
 *   • Primary  → Navy fill / white text · hover darker navy · subtle lift.
 *   • Secondary → White fill / navy border · hover light-navy tint.
 *   • Accent   → Vivid #2F80ED for high-emphasis moments (Add to Cart on PDP).
 *   • Ghost, link, destructive kept for admin utility.
 * Motion: transform + shadow only, transitions targeted (never `transition-all`).
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[14px] text-base font-semibold tracking-tight " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 focus-visible:ring-offset-white " +
  "disabled:pointer-events-none disabled:opacity-50 " +
  "transition-[transform,box-shadow,background-color,color,border-color] duration-200 ease-out " +
  "[&_svg]:pointer-events-none [&_svg]:size-[18px] [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-brand-navy text-white shadow-button hover:bg-brand-navy2 hover:-translate-y-[1px] hover:shadow-soft active:translate-y-0",
        accent:
          "bg-brand-accent text-white shadow-button hover:bg-[#1E6BD8] hover:-translate-y-[1px] hover:shadow-soft active:translate-y-0",
        secondary:
          "bg-white text-brand-navy border border-brand-navy hover:bg-brand-surface hover:-translate-y-[1px] active:translate-y-0",
        outline:
          "bg-white text-brand-navy border border-brand-border hover:border-brand-navy hover:bg-brand-surface",
        ghost:
          "text-brand-navy hover:bg-brand-surface",
        link:
          "text-brand-accent underline-offset-4 hover:underline",
        destructive:
          "bg-red-50 text-red-700 border border-red-200 hover:bg-red-100",
      },
      size: {
        default: "h-12 px-7 py-3",
        sm:      "h-10 px-5 py-2 text-sm rounded-[12px]",
        lg:      "h-14 px-9 py-4 text-base rounded-[16px]",
        xl:      "h-16 px-10 py-4 text-lg rounded-[18px]",
        icon:    "h-11 w-11 rounded-[12px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
